import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import CourseCascade from "./CourseCascade";

/**
 * LMS - Part 8 (Results) for Council/Admin + Faculty.
 *
 * Pick a course (Trade -> Sub-Trade -> Course), then an exam, to see who sat it
 * and their auto-graded scores. Exam-Wise results publish themselves on submit;
 * Semester/Yearly results wait here for a "Publish" (writes the official
 * marksheet). Faculty only see their assigned courses.
 */
export default function Results() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [data, setData] = useState(null); // { exam, can_publish, rows }
  const [busy, setBusy] = useState(false);

  const onCourse = async (cid) => {
    setExams([]);
    setExamId("");
    setData(null);
    if (!cid) return;
    try {
      const res = await dispatch(getDataAPI(`admin/lms/exams?course_id=${cid}`, token()));
      setExams(res.data.data || []);
    } catch (e) {
      alert({ error: "Could not load exams." });
    }
  };

  const loadAttempts = async (id) => {
    setExamId(id);
    setData(null);
    if (!id) return;
    try {
      const res = await dispatch(getDataAPI(`admin/lms/exam-attempts?exam_id=${id}`, token()));
      setData(res.data.data);
    } catch (e) {
      alert({ error: "Could not load results." });
    }
  };

  const publish = async (studentId) => {
    const what = studentId ? "this student's result" : "all pending results for this exam";
    if (!window.confirm(`Publish ${what}? It will be written to the official marksheet.`)) return;
    setBusy(true);
    try {
      const body = { exam_id: examId };
      if (studentId) body.student_id = studentId;
      const res = await dispatch(postDataAPI("admin/lms/exam-publish", body, token()));
      alert({ success: res.data.message || "Published." });
      loadAttempts(examId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not publish." });
    }
    setBusy(false);
  };

  const badge = (s) => {
    const map = {
      registered: "secondary",
      in_progress: "warning",
      submitted: "info",
      published: "success",
    };
    return <span className={`badge badge-${map[s] || "secondary"}`}>{s}</span>;
  };

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      <h4 className="mb-3">Results</h4>

      <CourseCascade onCourse={(id) => onCourse(id)} />

      {exams.length > 0 && (
        <div className="form-group" style={{ maxWidth: 480 }}>
          <label>Exam</label>
          <select className="form-control" value={examId} onChange={(e) => loadAttempts(e.target.value)}>
            <option value="">— select exam —</option>
            {exams.map((x) => (
              <option key={x.id} value={x.id}>
                {x.title} {x.subject_name ? `(${x.subject_name})` : "(Whole course)"} {x.exam_date ? `- ${x.exam_date}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {data && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ margin: 0 }}>
              {data.exam.title}{" "}
              <small className="text-muted">({data.rows.length} registered)</small>
            </h5>
            {data.can_publish && (
              <Button size="sm" variant="success" disabled={busy} onClick={() => publish(null)}>
                Publish all pending
              </Button>
            )}
          </div>
          <table className="table table-bordered bg-white">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Roll No</th>
                <th>Status</th>
                <th>Attempted</th>
                <th>Correct</th>
                <th>Wrong</th>
                <th>%</th>
                <th>Result</th>
                <th style={{ width: 120 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-muted">Nobody registered yet.</td></tr>
              ) : (
                data.rows.map((r, i) => (
                  <tr key={r.student_id}>
                    <td>{i + 1}</td>
                    <td>{r.name}</td>
                    <td>{r.roll_number}</td>
                    <td>{badge(r.status)}</td>
                    <td>{r.status === "registered" ? "—" : `${r.attempted}/${r.total_questions}`}</td>
                    <td>{r.status === "registered" ? "—" : r.correct}</td>
                    <td>{r.status === "registered" ? "—" : r.wrong}</td>
                    <td>{r.status === "registered" || r.status === "in_progress" ? "—" : `${r.percent}%`}</td>
                    <td>
                      {r.status === "published"
                        ? (r.passed ? <span className="text-success">Pass</span> : <span className="text-danger">Fail</span>)
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {r.status === "submitted" ? (
                        <Button size="sm" variant="outline-success" disabled={busy} onClick={() => publish(r.student_id)}>
                          Publish
                        </Button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <small className="text-muted">
            Exam-Wise results publish automatically when the student submits. Semester/Yearly
            results stay as <b>submitted</b> until you publish them to the official marksheet.
          </small>
        </>
      )}

      {!exams.length && <p className="text-muted">Choose a course to see its exam results.</p>}
    </div>
  );
}
